import { useState, useEffect } from 'react';
import { apiFetch, fetchBlob } from './api.js';

const store = { users: {}, userPromises: {}, avatarUrls: {}, avatarPromises: {}, listeners: new Set() };

function notify() { store.listeners.forEach(fn => fn()); }

function ensureUserLoaded(id) {
  if (store.users[id] || store.userPromises[id]) return;
  store.userPromises[id] = apiFetch(`/users/${id}`)
    .then(data => { if (data?.id) store.users[data.id] = { ...data, role: data.role ?? data.kind }; })
    .catch(() => {})
    .finally(notify);
}

function ensureAvatarLoaded(path) {
  if (!path || store.avatarUrls[path] || store.avatarPromises[path]) return;
  store.avatarPromises[path] = fetchBlob(path)
    .then(blob => { store.avatarUrls[path] = URL.createObjectURL(blob); })
    .catch(() => {})
    .finally(notify);
}

// Self-contained user fetch, independent of UserCacheContext - same rationale as
// selfLookups.js: a dynamically-loaded extension bundle's useContext() never sees
// the host's <UserCacheProvider> and silently gets the Context's default no-op
// fetchUser instead. useUser()/Avatar fall back to this so they render correctly
// no matter which bundle loaded them.
export function useSelfUser(id, enabled) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || !id) return undefined;
    ensureUserLoaded(id);
    const listener = () => setTick(t => t + 1);
    store.listeners.add(listener);
    return () => store.listeners.delete(listener);
  }, [id, enabled]);

  const user = id ? (store.users[id] ?? null) : null;

  useEffect(() => {
    if (enabled && user?.avatar_url) ensureAvatarLoaded(user.avatar_url);
  }, [enabled, user?.avatar_url]);

  return { user, avatarUrl: user?.avatar_url ? (store.avatarUrls[user.avatar_url] ?? null) : null };
}

// blob: URL for an avatar_url path (e.g. "/users/42/avatar"), fetched once and shared by
// every Avatar in this bundle - null until loaded, or when there is no path.
export function useAvatarUrl(path) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!path) return undefined;
    ensureAvatarLoaded(path);
    const listener = () => setTick(t => t + 1);
    store.listeners.add(listener);
    return () => store.listeners.delete(listener);
  }, [path]);

  return path ? (store.avatarUrls[path] ?? null) : null;
}

// Forgets the loaded picture of an avatar_url path and loads it again (after the user
// uploaded, replaced or removed it), so every Avatar showing it updates.
export function invalidateAvatar(path) {
  if (!path) return;
  delete store.avatarUrls[path];
  delete store.avatarPromises[path];
  notify();
}
